import { useEffect, useState } from "react";

/**
 * Confetti — one-shot celebration overlay. CSS-only, no libraries.
 * Renders 24 confetti pieces + a central check burst; auto-removes after 1.6s.
 * Usage: <Confetti trigger={showConfetti} onDone={() => setShowConfetti(false)} />
 */
export function Confetti({ trigger, onDone, duration = 1600 }) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!trigger) return;
        setVisible(true);
        const t = setTimeout(() => {
            setVisible(false);
            onDone?.();
        }, duration);
        return () => clearTimeout(t);
    }, [trigger, duration, onDone]);

    if (!visible) return null;

    const colours = ["#E02B2B", "#1E3355", "#EA580C", "#2563EB", "#0F766E", "#DB2777", "#CA8A04", "#65A30D"];
    const pieces = Array.from({ length: 26 }, (_, i) => {
        const c = colours[i % colours.length];
        const dx = (Math.cos((i / 26) * Math.PI * 2) * 220 + (i % 5 - 2) * 40).toFixed(0);
        const dy = (Math.sin((i / 26) * Math.PI * 2) * 220 + (i % 4 - 2) * 40).toFixed(0);
        const rot = ((i * 47) % 360).toFixed(0);
        const delay = ((i % 6) * 40).toFixed(0);
        return (
            <span
                key={i}
                aria-hidden
                className="fx-confetti-piece"
                style={{
                    background: c,
                    animationDelay: `${delay}ms`,
                    "--dx": `${dx}px`,
                    "--dy": `${dy}px`,
                    "--rot": `${rot}deg`,
                }}
            />
        );
    });

    return (
        <div className="fixed inset-0 pointer-events-none z-[9999] flex items-center justify-center" role="presentation" data-testid="confetti">
            <div className="relative">
                {pieces}
                <div className="fx-confetti-check">
                    <svg viewBox="0 0 64 64" width="80" height="80" className="drop-shadow-lg">
                        <circle cx="32" cy="32" r="30" fill="#10B981" />
                        <path d="M18 33 L28 43 L46 22" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default Confetti;
